import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Search } from "lucide-react";
import MandanteLogo from "@/components/MandanteLogo";

export default function AcreditacionTrabajadores() {
  const { id: mid, cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [mandante, setMandante] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [query, setQuery] = useState("");
  const [cargo, setCargo] = useState("");
  const canEdit = ["ADMIN", "HR_MANAGER", "RECRUITER", "SUPERVISOR"].includes(user?.role);

  const load = async () => {
    try {
      const [m, c, w] = await Promise.all([
        api.get("/mandantes").then((r) => r.data.find((x) => x.id === mid)),
        api.get(`/mandantes/${mid}/contratos/${cid}`).then((r) => r.data),
        api.get("/workers").then((r) => r.data),
      ]);
      if (!m || !c) { toast.error("Contrato no encontrado"); nav("/acreditacion"); return; }
      setMandante(m); setContrato(c);
      setWorkers(w.filter((x) => (x.mandante || x.company) === m.name && x.contrato === c.name));
    } catch (e) { toast.error("No se pudo cargar"); }
  };
  useEffect(() => { load(); }, [mid, cid]);

  if (!mandante || !contrato) return <div className="text-sm text-slate-500">Cargando…</div>;

  const riskColor = (p) => p >= 80 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";
  const cargos = Array.from(new Set(workers.map((w) => (w.position || "").trim()).filter(Boolean))).sort();
  const q = query.trim().toLowerCase();
  const filtered = workers.filter((w) => {
    const name = `${w.first_name || ""} ${w.last_name || ""} ${w.rut || ""}`.toLowerCase();
    const posOk = !cargo || (w.position || "").trim().toUpperCase() === cargo.toUpperCase();
    return posOk && (!q || name.includes(q));
  });

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <Link to="/acreditacion" className="hover:text-emerald-600 inline-flex items-center gap-1"><ChevronLeft size={14} /> Acreditación</Link>
        <ChevronRight size={14} />
        <Link to={`/acreditacion/${mid}`} className="hover:text-emerald-600">{mandante.name}</Link>
        <ChevronRight size={14} />
        <Link to={`/acreditacion/${mid}/contratos/${cid}`} className="hover:text-emerald-600">{contrato.name}</Link>
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium">Trabajadores</span>
      </nav>

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <MandanteLogo mandante={mandante} size="lg" />
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users size={24} className="text-blue-600" /> Trabajadores del contrato
            </h1>
            <p className="text-slate-500 text-sm mt-1">{contrato.name} · {mandante.name} · {workers.length} trabajador(es)</p>
          </div>
        </div>
        <Link to={`/acreditacion/${mid}/contratos/${cid}`} className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
          <ChevronLeft size={14} /> Volver al contrato
        </Link>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="trab-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o RUT…"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:border-indigo-400 outline-none" />
          </div>
          <select data-testid="trab-filter-cargo" value={cargo} onChange={(e) => setCargo(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Todos los cargos</option>
            {cargos.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-400 italic">
            {workers.length === 0 ? "Aún no hay trabajadores en este contrato." : "Sin resultados para los filtros aplicados."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="workers-table" className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-2 whitespace-nowrap">RUT</th>
                  <th className="px-5 py-2">Trabajador</th>
                  <th className="px-5 py-2">Cargo</th>
                  <th className="px-5 py-2 text-right whitespace-nowrap">Avance</th>
                  <th className="px-5 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((w) => (
                  <tr key={w.id} data-testid={`worker-row-${w.id}`} className="border-t border-slate-50 hover:bg-blue-50/40 cursor-pointer" onClick={() => nav(`/acreditacion/${mid}/contratos/${cid}/trabajadores/${w.id}`)}>
                    <td className="px-5 py-3 font-mono text-[11px] text-slate-700 whitespace-nowrap">{w.rut || "—"}</td>
                    <td className="px-5 py-3 font-semibold text-slate-800">{w.first_name} {w.last_name}</td>
                    <td className="px-5 py-3 text-slate-600 text-xs">{w.position || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${riskColor(w.progress_percentage || 0)}`} style={{ width: `${w.progress_percentage || 0}%` }} />
                        </div>
                        <span className="font-semibold text-blue-600 w-10 text-right">{w.progress_percentage || 0}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right"><ChevronRight size={14} className="text-slate-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
